// Alert severity — single source of truth for both notification surfaces
// (the מבט כללי overview grid and the ניהול התראות management cards).
//
// ENGINE CONVENTION (authoritative): each alert INSTANCE carries a raw integer
// severity written by the engine. Higher = more severe:
//   Low = -10, Medium = 0, High = 10.
// Zero is the engine default and means Medium — NOT the lowest bucket.
//
// Alert-TYPE records instead carry a coarse string `severityHint`
// (LOW/MILD/MEDIUM/HIGH/VERY_HIGH) set by the operator in the admin UI.
//
// Both representations are rendered through the Hebrew labels defined here so
// the two surfaces can never drift apart again.

export type SeverityBucket = 'low' | 'medium' | 'high'

// Exact Hebrew labels a severity can render as — keep in sync with
// AlertItem['severity'] (src/mockdata/notification/notifications.ts).
export type SeverityHebrew =
  | 'נמוכה'
  | 'קלה'
  | 'בינונית'
  | 'גבוהה'
  | 'גבוהה מאוד'

export const SEVERITY_HEBREW: Record<SeverityBucket, SeverityHebrew> = {
  low: 'נמוכה',
  medium: 'בינונית',
  high: 'גבוהה',
}

// Instance severity: bucket a raw engine integer.
// -10 → low, 0 → medium, 10 → high. Values in between/beyond degrade by sign;
// a missing value falls back to medium (the engine's stored default of 0).
export const severityBucketFromValue = (
  value: number | null | undefined
): SeverityBucket => {
  if (value == null || Number.isNaN(value)) return 'medium'
  if (value > 0) return 'high'
  if (value < 0) return 'low'
  return 'medium'
}

export const severityHebrewFromValue = (
  value: number | null | undefined
): SeverityHebrew => SEVERITY_HEBREW[severityBucketFromValue(value)]

// Integer sent to the /all severity filter — must equal the engine's stored
// values so the gateway's `alerts.severity = ?` comparison matches.
export const SEVERITY_FILTER_VALUE: Record<SeverityBucket, number> = {
  low: -10,
  medium: 0,
  high: 10,
}

// UI filter selection → request param. Filter widgets hold a bucket string
// ('low'/'medium'/'high') or an "all"/empty selection; translate that into the
// raw engine integer the /all endpoint filters on, or undefined (= no filter)
// for 'all'/null/unknown values.
export const severityParamFromBucket = (
  bucket: string | null | undefined
): number | undefined => {
  if (!bucket) return undefined
  return SEVERITY_FILTER_VALUE[bucket as SeverityBucket] ?? undefined
}

// Alert-TYPE severityHint enum (5 admin buckets → 3 display buckets).
export type SeverityHint = 'LOW' | 'MILD' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH'

// Full-fidelity Hebrew labels for the 5-value hint enum (management cards keep
// the extra "קלה"/"גבוהה מאוד" granularity the admin form offers).
export const SEVERITY_HINT_HEBREW: Record<SeverityHint, SeverityHebrew> = {
  LOW: 'נמוכה',
  MILD: 'קלה',
  MEDIUM: 'בינונית',
  HIGH: 'גבוהה',
  VERY_HIGH: 'גבוהה מאוד',
}

export const SEVERITY_HINT_BUCKET: Record<SeverityHint, SeverityBucket> = {
  LOW: 'low',
  MILD: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  VERY_HIGH: 'high',
}

// A type with no severityHint set defaults to Medium — matching the engine's
// default instance severity of 0 so a type and its instances agree.
export const severityHebrewFromHint = (
  hint: string | null | undefined
): SeverityHebrew => {
  if (!hint) return SEVERITY_HEBREW.medium
  return (
    SEVERITY_HINT_HEBREW[hint.toUpperCase() as SeverityHint] ??
    SEVERITY_HEBREW.medium
  )
}

export const severityBucketFromHint = (
  hint: string | null | undefined
): SeverityBucket => {
  if (!hint) return 'medium'
  return SEVERITY_HINT_BUCKET[hint.toUpperCase() as SeverityHint] ?? 'medium'
}

// The severityHint an alert type gets when its tree is saved: the highest severity of any outcome,
// bucketed like an instance. The editor writes three levels, so the hint is LOW, MEDIUM or HIGH.
type OutcomeHolder = {
  outcome?: { severity: number }
  children?: OutcomeHolder[]
}

export const severityHintFromTree = (tree: {
  root: OutcomeHolder
}): 'LOW' | 'MEDIUM' | 'HIGH' => {
  let highest: number | undefined
  const walk = (node: OutcomeHolder) => {
    if (node.outcome) {
      highest = Math.max(
        highest ?? node.outcome.severity,
        node.outcome.severity
      )
    }
    node.children?.forEach(walk)
  }
  walk(tree.root)
  return severityBucketFromValue(highest).toUpperCase() as
    | 'LOW'
    | 'MEDIUM'
    | 'HIGH'
}
